import { Helmet } from 'react-helmet-async';
import { useLanguageContext } from '@/i18n/language-context';

interface SeoHeadProps {
  title: string;
  description: string;
  canonicalPath: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SeoHead = ({
  title,
  description,
  canonicalPath,
  noindex = false,
  ogImage = '/logo_512.png',
  ogType = 'website',
  jsonLd,
}: SeoHeadProps) => {
  const { currentLanguage } = useLanguageContext();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonicalUrl = `${origin}${canonicalPath}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${origin}${ogImage}`;
  const langCode = currentLanguage.split('-')[0];
  const alternateLang = currentLanguage === 'en-US' ? 'de-DE' : 'en-US';
  const alternateLangCode = alternateLang.split('-')[0];

  return (
    <Helmet>
      <html lang={langCode} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang */}
      <link rel="alternate" hrefLang={langCode} href={canonicalUrl} />
      <link rel="alternate" hrefLang={alternateLangCode} href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="SELISE Legal Templates" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
};
